import React, { ComponentType, lazy, LazyExoticComponent, ReactNode } from 'react';
import { UserRole } from 'api';
import { OverlayLoader } from 'components/ui/OverayLoader/OverlayLoader';
/** Везде, где подключается <Router routes={routes}/>, кроме App.tsx, routes: IRoute[] берется из пропсов IProps. */

export interface IRoute {
  /** Адрес */
  path: string;
  /** Точность совпадения */
  exact: boolean;
  /** Защищенный роут */
  private?: boolean;
  /** Компонент */
  component?: LazyExoticComponent<ComponentType<React.ReactNode>>;
  /** Для каких ролей доступен данный роут */
  roleGuard?: UserRole[];
  /** Дочерние роуты */
  routes?: IRoute[];
  /** Редирект */
  redirect?: string;
  /** Прелоудер ф */
  fallback: NonNullable<ReactNode> | null;
}

export const fallbackComponent = <OverlayLoader />;

export enum ERouteUrl {
  HOME = '/',
  LOGIN = '/login',
  RESTORE_PASSWORD = '/restore-password',
  USER_LIST = '/user-list',

  PERMISSIONS = '/permissions',

  LZ = '/lz',
  LZ_LIST = '/lz/list',
  LZ_CREATE = '/lz/create',
  LZ_INFO = '/lz/:id/info',
  LZ_PZU = '/lz/:id/pzu',
  LZ_PRICES = '/lz/:id/prices',
  LZ_METAL = '/lz/:id/metals',
  LZ_BUSINESS_CARD = '/lz/:id/business-card',
  LZ_BUSINESS_ID = '/lz/:id/businessId',
  LZ_ANALYTICS = '/lz/:id/analytics',
  LZ_INFO_ANALYTICS = '/lzInfo/:id/analytics',
  LZ_APP_INFO = '/lzInfo/:id',
  LZ_INFO_TRANSACTIONS = '/lzInfo/:id/transactions',
  LZ_INFO_PAYMENTS = '/lzInfo/:id/payments',
  LZ_INFO_USERS = '/lzInfo/:id/users',
  LZ_SBER_API = '/lz/:id/sber-api/:status?',

  PZU = '/pzu',
  PZU_INFO = '/pzu/:id/info',
  PZU_PRICES = '/pzu/:id/prices',
  PZU_PERSONALS = '/pzu/:id/personals',
  PZU_BUSINESS_CARD = '/pzu/:id/business-card',
  PZU_LIST = '/pzu/list',
  PZU_CREATE = '/pzu/create',

  PSA = '/psa',
  PSA_ID = '/psa/:id/:lzId?',
  PSA_LIST = '/psa/list',
  PSA_CREATE = '/psa/create',

  PAYMENT = '/payment',
  PAYMENT_LIST = '/payment/list',
  PAYMENT_ID = '/payment/:id/:lzId?',
  PAYMENT_PRINT = '/print/:id',
  PAYMENT_AGREEMENT = '/print/:id/agreement/:psaId',
  PAYMENT_INFO_PRINT = '/print/payment-info/:id',

  TRANSACTION = '/transaction',
  TRANSACTION_LIST = '/transaction/list',
  TRANSACTION_ID = '/transaction/:id/:lzIds?',

  LS = '/ls',
  LS_LIST = '/ls/list',
  LS_TRANSACTION_LIST = '/ls/transaction/:id?',

  GROUP_COMMISSION = '/group-commissions',
  GROUP_COMMISSION_ITEM = '/group-commissions/:id',

  NOT_FOUND = '/*',
  NOT_FOUND_PAGE = '/404',
  MANUAL = '/manual',
  SBER_ID = '/sberid/:status?',
  PASSPORT_INFO = '/passportInfo/id=:id/psa=:psaId',
  NEWS = '/news',
  HELP = '/help',

  RISK_MANEGMENT = '/risk-manegment',
  RISK_MANEGMENT_COUNTERAGENTS = '/risk-manegment/:id/counteragents',
  OPERATIONS = '/operations',
  OPERATIONS_PSA_LIST = '/operations/psa-list',
  OPERATIONS_PAYMENT_LIST = '/operations/payment-list',
  OPERATIONS_GROUP_COMMISSIONS = '/operations/group-commissions',
  OPERATIONS_SBER_ID = '/operations/sber-payments',
  PARTY_PAYMENTS = '/party-payments/list',
  INDEXES = '/indexes',
  SETTINGS = '/settings',
  UPLOAD_MOBILE_SCANS = '/mobile/upload',
}

export const routes = [
  {
    path: ERouteUrl.HOME,
    exact: true,
    private: false,
    redirect: ERouteUrl.LOGIN,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.LOGIN,
    component: lazy(() => import('../components/pages/Auth/Login')),
    exact: true,
    private: false,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.USER_LIST,
    component: lazy(() => import('components/pages/User/User')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.TechSupport],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.LZ_LIST,
    component: lazy(() => import('../components/pages/Lz/LzList')),
    exact: true,
    private: true,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.AppViewer, UserRole.TechSupport],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.LZ_APP_INFO,
    component: lazy(() => import('components/pages/Lz/LzTab/LzTabContainer')),
    exact: false,
    private: false,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.AppViewer, UserRole.TechSupport],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.LZ,
    component: lazy(() => import('components/pages/Lz/LzRouter')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.AppViewer, UserRole.TechSupport],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.LZ_PRICES,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_METAL,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_SBER_API,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_METAL,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_BUSINESS_CARD,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_BUSINESS_ID,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [UserRole.LzAdmin],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_CREATE,
        component: lazy(() => import('../components/pages/Lz/LzTab/LzTab')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.TechSupport],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_LIST,
        component: lazy(() => import('../components/pages/Lz/LzList')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_INFO,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_PZU,
        component: lazy(() => import('components/pages/Pzu/PzuList/PzuListContainer')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppViewer, UserRole.AppAdmin, UserRole.TechSupport],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ_ANALYTICS,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppViewer, UserRole.LzAdmin],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LZ,
        component: lazy(() => import('../components/pages/NotFound')),
        exact: false,
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.PZU,
    component: lazy(() => import('../components/pages/Pzu/PzuRouter')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.AppViewer, UserRole.TechSupport],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.PZU_CREATE,
        component: lazy(() => import('components/pages/Pzu/PzuTab/PzuTab')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PZU_LIST,
        component: lazy(() => import('components/pages/Pzu/PzuList/PzuListContainer')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PZU_INFO,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PZU_PRICES,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.AppViewer],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PZU_PERSONALS,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PZU_BUSINESS_CARD,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PZU,
        component: lazy(() => import('../components/pages/NotFound')),
        exact: false,
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.PSA,
    component: lazy(() => import('components/pages/Psa/PsaRouter')),
    exact: false,
    private: true,
    roleGuard: [
      UserRole.AppAdmin,
      UserRole.LzAdmin,
      UserRole.PzuCashier,
      UserRole.PzuInspector,
      UserRole.LzAccountant,
      UserRole.AppViewer,
      UserRole.TechSupport,
    ],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.PSA_CREATE,
        component: lazy(() => import('components/pages/Psa/CreatePsa/CreatePsa')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.PzuCashier, UserRole.PzuInspector],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PSA_ID,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.PzuInspector,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.PSA,
        component: lazy(() => import('components/pages/NotFound')),
        exact: false,
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.GROUP_COMMISSION,
    component: lazy(() => import('components/pages/GroupCommissions/GroupCommissionsRouter')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.GROUP_COMMISSION_ITEM,
        component: lazy(() => import('components/pages/GroupCommissions/GroupCommissionsItem')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin],
        fallback: fallbackComponent,
      },
      // {
      //   path: ERouteUrl.PSA,
      //   component: lazy(() => import('components/pages/NotFound')),
      //   exact: false,
      //   fallback: fallbackComponent,
      // },
    ],
  },
  {
    path: ERouteUrl.OPERATIONS,
    component: lazy(() => import('components/pages/Psa/PsaRouter')),
    exact: false,
    private: false,
    roleGuard: [
      UserRole.AppAdmin,
      UserRole.LzAdmin,
      UserRole.PzuCashier,
      UserRole.PzuInspector,
      UserRole.LzAccountant,
      UserRole.AppViewer,
      UserRole.TechSupport,
    ],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.OPERATIONS_PSA_LIST,
        component: lazy(() => import('components/pages/psaPaymentContainer/PsaPaymentContainer')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.PzuInspector,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.OPERATIONS_PAYMENT_LIST,
        component: lazy(() => import('components/pages/psaPaymentContainer/PsaPaymentContainer')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.PzuInspector,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.OPERATIONS_GROUP_COMMISSIONS,
        component: lazy(() => import('components/pages/psaPaymentContainer/PsaPaymentContainer')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.PzuInspector,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.OPERATIONS_SBER_ID,
        component: lazy(() => import('components/pages/psaPaymentContainer/PsaPaymentContainer')),
        exact: true,
        private: true,
        roleGuard: [UserRole.LzAdmin],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.OPERATIONS,
        component: lazy(() => import('components/pages/NotFound')),
        exact: false,
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.TRANSACTION,
    component: lazy(() => import('components/pages/Transaction/TransactionRouter')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppViewer],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.TRANSACTION_LIST,
        component: lazy(() => import('components/pages/Transaction/TransactionList/TransactionListContainer')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppViewer],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.TRANSACTION_ID,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppViewer],
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.LS,
    component: lazy(() => import('components/pages/Ls/LsRouter')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppViewer],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.LS_LIST,
        component: lazy(() => import('components/pages/Ls/LsList/LsListContainer')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppViewer],
        fallback: fallbackComponent,
      },
      {
        path: ERouteUrl.LS_TRANSACTION_LIST,
        component: lazy(() => import('components/pages/Ls/LsList/LsTransactionListContainer')),
        exact: true,
        private: true,
        roleGuard: [UserRole.AppViewer],
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.PAYMENT,
    component: lazy(() => import('components/pages/Payment/PaymentRouter')),
    exact: false,
    private: true,
    roleGuard: [
      UserRole.AppAdmin,
      UserRole.LzAdmin,
      UserRole.PzuCashier,
      UserRole.LzAccountant,
      UserRole.AppViewer,
      UserRole.TechSupport,
    ],
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.PAYMENT_ID,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.LzAccountant,
          UserRole.AppViewer,
          UserRole.TechSupport,
        ],
        fallback: fallbackComponent,
      },

      {
        path: ERouteUrl.PAYMENT,
        component: lazy(() => import('components/pages/NotFound')),
        exact: false,
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.PAYMENT_PRINT,
    component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
    exact: true,
    private: true,
    roleGuard: [
      UserRole.AppAdmin,
      UserRole.LzAdmin,
      UserRole.PzuCashier,
      UserRole.LzAccountant,
      UserRole.PzuInspector,
      UserRole.AppViewer,
    ],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.PAYMENT_INFO_PRINT,
    component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
    exact: true,
    private: true,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.PzuCashier],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.PAYMENT_AGREEMENT,
    component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
    exact: true,
    private: true,
    roleGuard: [UserRole.LzAdmin, UserRole.PzuCashier, UserRole.LzAccountant, UserRole.PzuInspector],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.PASSPORT_INFO,
    component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
    exact: true,
    private: true,
    roleGuard: [
      UserRole.AppAdmin,
      UserRole.AppViewer,
      UserRole.LzAdmin,
      UserRole.PzuCashier,
      UserRole.LzAccountant,
      UserRole.PzuInspector,
    ],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.PERMISSIONS,
    component: lazy(() => import('../components/pages/Permissions')),
    exact: true,
    private: true,
    roleGuard: [UserRole.AppAdmin],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.RESTORE_PASSWORD,
    component: lazy(() => import('../components/pages/Auth/RestorePassword/RestorePassword')),
    exact: true,
    private: false,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.SBER_ID,
    component: lazy(() => import('../components/pages/Status/Status')),
    exact: false,
    private: false,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.NEWS,
    component: lazy(() => import('../components/pages/News/NewsContainer')),
    exact: false,
    private: false,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.UPLOAD_MOBILE_SCANS,
    component: lazy(() => import('../components/template/UploadScans/UploadScans')),
    exact: false,
    private: false,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.HELP,
    component: lazy(() => import('../components/pages/Help/HelpContainer')),
    exact: false,
    private: false,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.SETTINGS,
    component: lazy(() => import('../components/pages/Settings/SettingsContainer')),
    exact: false,
    private: false,
    fallback: fallbackComponent,
    roleGuard: [UserRole.AppAdmin],
  },
  {
    path: ERouteUrl.RISK_MANEGMENT,
    component: lazy(() => import('components/pages/RiskManegment/RiskManegmentRouter')),
    exact: false,
    private: false,
    fallback: fallbackComponent,
    routes: [
      {
        path: ERouteUrl.RISK_MANEGMENT_COUNTERAGENTS,
        component: lazy(() => import('components/layout/RouteIdGuard/RouteIdGuard')),
        exact: true,
        private: true,
        roleGuard: [UserRole.LzAdmin, UserRole.AppViewer],
        fallback: fallbackComponent,
      },
    ],
  },
  {
    path: ERouteUrl.PARTY_PAYMENTS,
    component: lazy(() => import('components/pages/PartyPayments/PartyPayments')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.TechSupport],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.INDEXES,
    component: lazy(() => import('components/pages/Indexes/Indexes')),
    exact: false,
    private: true,
    roleGuard: [UserRole.AppViewer],
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.NOT_FOUND,
    component: lazy(() => import('../components/pages/NotFound')),
    exact: false,
    fallback: fallbackComponent,
  },
  {
    path: ERouteUrl.NOT_FOUND_PAGE,
    component: lazy(() => import('../components/pages/NotFound')),
    exact: false,
    fallback: fallbackComponent,
  },
];
